import { Button } from "@components/controls/Button"
import { Field } from "@components/controls/Field"
import { createTsForm, useFieldInfo, useTsController } from "@ts-react/form"
import type { RTFFormSchemaType, RenderedFieldMap } from "@ts-react/form/lib/src/createSchemaForm"
import { ComponentProps, Fragment, PropsWithChildren, ReactElement, ReactNode, createContext, useContext, useMemo } from "react"
import { z } from "zod"
import init from "zod-empty"
import { defaultMapping, fieldTypes } from "./form-support"
import { SubmitBar } from "@components/controls/SubmitBar"
import { twMerge } from "tailwind-merge"

export { defaultMapping, fieldTypes }

export const FormLayout = {
  Heading(props: PropsWithChildren<{ className?: string; large?: boolean }>) {
    return <h3 className={twMerge("border-b border-black mb-4", props.large ? "type-title-l-medium pb-4" : "type-title-xs-medium pb-3", props.className)}>{props.children}</h3>
  },
  Describe(props: PropsWithChildren<DescriptionValue>) {
    return <DescriptionContext.Provider value={props}>{props.children}</DescriptionContext.Provider>
  },
  Row(props: PropsWithChildren<{}>) {
    return <div className="stack-x-4">{props.children}</div>
  },
  Spacer() {
    return <div className="h-7" />
  },
  Stack(props: PropsWithChildren<{}>) {
    return <div className="stack-y-5">{props.children}</div>
  },
  Note(props: PropsWithChildren<{}>) {
    return <div className="type-ui-s-roman">{props.children}</div>
  },
  Field: Field,
  Error(props: PropsWithChildren<{}>) {
    return <div className="text-error-dark">{props.children}</div>
  },
  Grid(props: PropsWithChildren<{}>) {
    return <div className="field-grid">{props.children}</div>
  },
  SubmitBar(props: ComponentProps<typeof SubmitBar>) {
    const ctx = useContext(FormContext)

    return <SubmitBar submitting={ctx.loading} submitLabel={props.submitLabel ?? ctx.submitLabel ?? "Submit"} {...props} />
  },
}

// const SubmitBarContainer = styled("div", {
//   display: "flex",
//   gap: 2,
//   flexDirection: "column",
//   "[data-part='right']": {
//     display: "flex",
//     flexDirection: "row",
//     gap: "$2",
//     "> button": {
//       flex: "1 1 auto",
//     },
//   },
//   "@container (width > 400px)": {
//     flexDirection: "row",
//   },
//   variants: {
//     submitOnly: {
//       true: {},
//       false: {
//         justifyContent: "space-between",
//       },
//     },
//   },
// })

export function useDescription() {
  const contextValue = useContext(DescriptionContext)
  const descriptionParts = useFieldInfo().zodType.description?.split(/\s*\/\/\s*/g)
  return {
    label: contextValue.label ?? descriptionParts?.[0],
    placeholder: contextValue.placeholder ?? descriptionParts?.[1],
    note: contextValue.note ?? descriptionParts?.[2],
  }
}

type DescriptionValue = { label?: ReactNode; placeholder?: string; note?: ReactNode }

const DescriptionContext = createContext<DescriptionValue>({})

type Props<TSchema extends RTFFormSchemaType> = {
  schema: TSchema
  data?: Partial<z.infer<TSchema>>
  onSubmit: (data: z.infer<TSchema>) => void
  loading?: boolean
  mapping?: readonly [z.ZodType<any>, React.ComponentType][]
  submitLabel?: string
  render?: (fields: RenderedFieldMap<TSchema>, layouts: typeof FormLayout) => ReactElement
}

export function ZodForm<T extends RTFFormSchemaType>(props: Props<T>) {
  const Form = useMemo(() => {
    return createTsForm([...(props.mapping ?? []), ...defaultMapping] as any, { FormComponent: FormContainer })
  }, [])

  const defaultValues = useMemo(() => {
    return {
      ...init(props.schema as any),
      ...props.data,
    }
  }, [props.data])

  return (
    <FormContext.Provider
      value={{
        loading: props.loading,
        submitLabel: props.submitLabel,
      }}
    >
      <Form<any>
        formProps={{
          loading: props.loading,
        }}
        schema={props.schema}
        defaultValues={defaultValues}
        onSubmit={props.onSubmit}
        children={
          props.render
            ? (fields: any) => <>{props.render!(fields as any, FormLayout)}</>
            : (fields) => (
                <FormLayout.Stack>
                  {Object.values(fields).map((item, i) => (
                    <Fragment key={i}>{item as any}</Fragment>
                  ))}
                  <FormLayout.SubmitBar />
                </FormLayout.Stack>
              )
        }
      />
    </FormContext.Provider>
  )
}

function FormContainer(props: { onSubmit: () => void; children: ReactNode; loading?: boolean }) {
  return (
    <form onSubmit={props.onSubmit} className="@container" noValidate>
      {props.children}
      {/* <Flex gap={2}>
        {props.extraLink}
        <Button type="submit" loading={props.loading} primary>
          {props.submitLabel ?? "Submit"}
        </Button>
        {props.extraButtons}
      </Flex> */}
    </form>
  )
}

type FormContext = {
  loading?: boolean
  submitLabel?: string
}

const FormContext = createContext<FormContext>(undefined!)
